import { useEffect, useRef, useState } from 'react';
import { useConfigStore } from '../../Stores/Config';
import './EmailUpvoteDialog.scss';
import { useSessionStore } from '../../Stores/Session';
import { focusWithoutScroll } from '../../Helper/ScrollHelper';

interface EmailUpvoteDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (email: string) => void;
}

const EmailUpvoteDialog = ({ open, onClose, onSubmit }: EmailUpvoteDialogProps) => {
    const session = useSessionStore(state => state.session);
    const setSession = useSessionStore(state => state.setSession);
    const [email, setEmail] = useState('');
    const configStore = useConfigStore();
    const inputRef = useRef<HTMLInputElement>(null);

    // Focus without preventScroll would scroll the host page (cross-frame focus
    // scroll on Safari). Replaces the native `autoFocus` attribute.
    useEffect(() => {
        if (open) {
            focusWithoutScroll(inputRef.current);
        }
    }, [open]);

    if (!open) return null;

    const handleSubmit = () => {
        if (email.trim()) {
            onSubmit(email.trim());
            setEmail('');
            onClose();
            setSession({ ...session, email: email.trim() });
        }
    };

    return (
        <div className="email-upvote-dialog-overlay" onClick={onClose}>
            <div className="email-upvote-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="email-upvote-dialog-title">
                    {configStore.translateText('enterYourEmail')}
                </div>
                <input
                    ref={inputRef}
                    className="email-upvote-dialog-input"
                    type="email"
                    aria-label={configStore.translateText('enterYourEmail')}
                    aria-required={true}
                    placeholder={configStore.translateText('email')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                />
                <div className="email-upvote-dialog-actions">
                    <button
                        className="email-upvote-dialog-cancel"
                        onClick={onClose}
                    >
                        {configStore.translateText('cancel')}
                    </button>
                    <button
                        className="email-upvote-dialog-submit"
                        onClick={handleSubmit}
                        disabled={!email.trim()}
                    >
                        {configStore.translateText('submit')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailUpvoteDialog;
